import * as Sentry from '@sentry/react';

const API_BASE = import.meta.env.VITE_API_URL || '/api';

interface RequestOptions {
  method?: string;
  body?: unknown;
  params?: Record<string, string>;
}

let isRefreshing = false;
let refreshQueue: Array<{ resolve: () => void; reject: (err: Error) => void }> = [];

function processRefreshQueue(error: Error | null) {
  refreshQueue.forEach(({ resolve, reject }) => {
    if (error) {
      reject(error);
    } else {
      resolve();
    }
  });
  refreshQueue = [];
}

class ApiClient {
  async request<T>(path: string, options: RequestOptions = {}): Promise<T> {
    const { method = 'GET', body, params } = options;

    let url = `${API_BASE}${path}`;
    if (params) {
      const searchParams = new URLSearchParams(params);
      url += `?${searchParams.toString()}`;
    }

    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };

    const res = await fetch(url, {
      method,
      headers,
      body: body ? JSON.stringify(body) : undefined,
      credentials: 'include',
    });

    if (res.status === 401) {
      const data = await res.json().catch(() => ({}));

      if (data.code === 'TOKEN_EXPIRED' && path !== '/auth/refresh') {
        return this.handleTokenRefresh<T>(path, options);
      }

      if (path !== '/auth/me') {
        window.location.href = '/login';
      }
      throw new Error('Unauthorized');
    }

    const data = await res.json();

    if (!res.ok) {
      const err = new Error(data.error || data.message || 'Request failed');
      Sentry.captureException(err, {
        extra: { path, method, status: res.status },
      });
      throw err;
    }

    return data as T;
  }

  private async handleTokenRefresh<T>(path: string, options: RequestOptions): Promise<T> {
    if (isRefreshing) {
      return new Promise<void>((resolve, reject) => {
        refreshQueue.push({ resolve, reject });
      }).then(() => this.request<T>(path, options));
    }

    isRefreshing = true;

    try {
      await fetch(`${API_BASE}/auth/refresh`, {
        method: 'POST',
        credentials: 'include',
      });

      processRefreshQueue(null);
      return this.request<T>(path, options);
    } catch (err) {
      processRefreshQueue(err instanceof Error ? err : new Error('Refresh failed'));
      window.location.href = '/login';
      throw err;
    } finally {
      isRefreshing = false;
    }
  }

  // Auth
  login(email: string, password: string) {
    return this.request<{ user: User }>('/auth/login', {
      method: 'POST',
      body: { email, password },
    });
  }

  logout() {
    return this.request('/auth/logout', { method: 'POST' });
  }

  me() {
    return this.request<{ user: User }>('/auth/me');
  }

  // Dashboard
  getDashboardSummary() {
    return this.request<DashboardSummary>('/out-of-stock/summary');
  }

  getDashboardOverview(range: string = '30') {
    return this.request<DashboardData>('/dashboard/overview', { params: { range } });
  }

  getMetrics(params?: { severity?: string; search?: string; active_only?: string; missingField?: string }) {
    return this.request<Metric[]>('/out-of-stock/metrics', { params });
  }

  getExcludedMetrics(params?: { severity?: string; search?: string }) {
    return this.request<Metric[]>('/out-of-stock/excluded-metrics', { params });
  }

  getSlowMovers() {
    return this.request<SlowMoversResponse>('/out-of-stock/slow-movers');
  }

  // Master data coverage
  getMasterDataCoverage() {
    return this.request<MasterDataCoverage>('/master-data/coverage');
  }

  // Products
  getProductDetail(sku: string) {
    return this.request<ProductDetail>(`/products/${encodeURIComponent(sku)}`);
  }

  getCalculationTrace(sku: string) {
    return this.request<CalculationTrace>(`/products/${encodeURIComponent(sku)}/calculation-trace`);
  }

  updateProductOverrides(sku: string, overrides: ProductOverrides & { isExcludedFromPlanning?: boolean; trackedViaParentOnly?: boolean }) {
    return this.request(`/products/${encodeURIComponent(sku)}/overrides`, {
      method: 'PUT',
      body: overrides,
    });
  }

  // Alerts
  getAlerts() {
    return this.request<Alert>('/alerts');
  }
  suppressAlert(data: { days: number; alertId: string }) {
    return this.request<SuppressedAlert>(`/alerts/${encodeURIComponent(data.alertId)}/suppress`, { method: 'POST', body: data });
  }
  unsuppressAlert(alertId: string) {
    return this.request<SuppressedAlert>(`/alerts/${encodeURIComponent(alertId)}/unsuppress`, { method: 'POST' });
  }

  // Settings
  getSettings() {
    return this.request<Settings>('/settings');
  }

  updateGeneralSettings(data: { notificationEmail: string; timezone: string; monitoredProductCategoryIds: string[]; notificationIntervalHours?: number; adhocNotificationsEnabled?: boolean }) {
    return this.request('/settings', { method: 'PUT', body: data });
  }

  updateParameters(data: ParameterSettings) {
    return this.request('/settings/parameters', { method: 'PUT', body: data });
  }

  updateWeclapp(data: { baseUrl: string; apiToken: string }) {
    return this.request('/settings/weclapp', { method: 'PUT', body: data });
  }

  testWeclapp() {
    return this.request<{ success: boolean; message: string; articleCount?: number }>('/settings/weclapp/test', { method: 'POST' });
  }

  // Warehouses
  getWarehouses() {
    return this.request<Warehouse[]>('/warehouses');
  }

  updateWarehousesMonitored(warehouses: { id: string; monitored: boolean }[]) {
    return this.request('/warehouses/monitored', { method: 'PUT', body: { warehouses } });
  }

  updateShippingWarehouses(warehouses: { id: string; shippingWarehouse: boolean }[]) {
    return this.request('/warehouses/shipping', { method: 'PUT', body: { warehouses } });
  }

  // Jobs
  triggerSync() {
    return this.request<{ sync: string; compute: { success: boolean; metrics_count: number } }>('/jobs/sync', { method: 'POST' });
  }

  // Jobs
  triggerCompution() {
    return this.request<{ sync: string; compute: { success: boolean; metrics_count: number } }>('/jobs/compute', { method: 'POST' });
  }

  triggerCompute() {
    return this.request<{ success: boolean; metrics_count: number }>('/jobs/compute', { method: 'POST' });
  }

  // Purchase Orders
  createPurchaseOrder(skus: string[]) {
    return this.request<PurchaseOrderDetail>('/purchase-orders', { method: 'POST', body: { skus } });
  }

  getPurchaseOrders() {
    return this.request<PurchaseOrderDetail[]>('/purchase-orders');
  }

  getPurchaseOrder(id: string) {
    return this.request<PurchaseOrderDetail>(`/purchase-orders/${encodeURIComponent(id)}`);
  }

  updatePurchaseOrderStatus(id: string, action: 'markAsOrdered' | 'archive') {
    return this.request<PurchaseOrderDetail>(`/purchase-orders/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      body: { action },
    });
  }

  updatePurchaseOrderItemQty(id: string, sku: string, orderedQty: number) {
    return this.request<PurchaseOrderDetail>(
      `/purchase-orders/${encodeURIComponent(id)}/items/${encodeURIComponent(sku)}`,
      { method: 'PATCH', body: { orderedQty } }
    );
  }

  checkOpenPurchaseOrders() {
    return this.request<{ open: Array<{ id: string; status: string; createdAt: string }> }>('/purchase-orders/check-open');
  }
}

// Types
export interface User {
  id: string;
  email: string;
  name: string;
  role: string;
  tenantId: string;
  tenantName: string;
}

export interface DashboardSummary {
  red: number;
  yellow: number;
  green: number;
  totalProducts: number;
  lastSyncAt: string | null;
  syncStatus: string;
  totalRiskContributionMargin: number;
  totalRiskRevenue: number;
  slowMoverKapitalbindung: number;
  slowMoverCount: number;
  nextOosTop5: Array<{
    sku: string;
    name: string;
    severity: 'red' | 'yellow' | 'green';
    effectiveSeverity: 'red' | 'yellow' | 'green';
    coverageDays: number;
    oosDate: string;
    riskContributionMargin: number | null;
    riskValue: number | null;
    usedAsComponent: boolean;
    topLevel: boolean;
    hasComponents: boolean;
  }>;
}

export interface DashboardData {
  current: {
    lagerrisiken: {
      criticalCount: number;
      warningCount: number;
      okCount: number;
      endangeredRevenue: number;
      endangeredMargin: number;
    };
    ueberbestand: {
      slowMoverCount: number;
      capitalBound: number;
    };
  };
  lastSync: {
    date: string | null;
    status: string;
  };
  history: Array<{
    date: string;
    criticalCount: number;
    endangeredMargin: number;
    slowMoverCount: number;
    capitalBound: number;
  }>;
}

export interface OrderQtyBreakdownEntry {
  parentSku: string;
  parentName: string | null;
  parentQty: number;
  factor: number;
  contribution: number;
}

export interface RecentSpike {
  date: string;
  originalQty: number;
  cappedQty: number;
}

export interface Metric {
  sku: string;
  name: string;
  /** Where the onHand actually lies. null when nothing is attributable. */
  warehouseBreakdown: WarehouseBreakdownEntry[] | null;
  severity: 'red' | 'yellow' | 'green';
  effectiveSeverity: 'red' | 'yellow' | 'green';
  dailyUsage: number;
  onHand: number;
  onOrder: number;
  availableQty: number;
  coverageDays: number;
  oosDate: string | null;
  orderByDate: string | null;
  minStockBreached: boolean;
  recommendationBlockedByOverduePo: boolean;
  recommendedOrderQty: number;
  orderQtyBreakdown: OrderQtyBreakdownEntry[] | null;
  recentSpikes: RecentSpike[] | null;
  riskValue: number | null;
  riskContributionMargin: number | null;
  dbPerUnit: number | null;
  usedAsComponent: boolean;
  topLevel: boolean;
  hasComponents: boolean;
  components: Array<{
    sku: string;
    name: string | null;
    severity: 'red' | 'yellow' | 'green' | null;
    effectiveSeverity: 'red' | 'yellow' | 'green' | null;
    dailyUsage: number | null;
    onHand: number | null;
    onOrder: number | null;
    availableQty: number | null;
    coverageDays: number | null;
    oosDate: string | null;
    orderByDate: string | null;
    minStockBreached: boolean;
    recommendedOrderQty: number | null;
    orderQtyBreakdown: OrderQtyBreakdownEntry[] | null;
    recentSpikes: RecentSpike[] | null;
    riskValue: number | null;
    riskContributionMargin: number | null;
    dbPerUnit: number | null;
    usedAsComponent: boolean;
    topLevel: boolean;
    hasComponents: boolean;
  }> | null;
  packagingUnits?: Array<{
    sku: string;
    name: string | null;
    quantity: number;
    avgDailyUsageVe: number;
    avgDailyUsageBase: number;
  }>;
}

/** Physical stock in all warehouses, independently of shipping stock used for planning. */
export interface WarehouseBreakdownEntry {
  shippingWarehouse: boolean;
  warehouseId: string | null;
  warehouseName: string | null;
  onHand: number;
}

export interface SlowMoverItem {
  sku: string;
  name: string;
  onHand: number;
  coverageDays: number;
  daysSinceLastActivity: number | null;
  purchasePrice: number | null;
  kapitalbindung: number | null;
  supplierName: string | null;
  warehouseBreakdown: WarehouseBreakdownEntry[] | null;
}

export interface SlowMoversResponse {
  items: SlowMoverItem[];
  summary: {
    totalKapitalbindung: number;
    count: number;
    avgCoverageDays: number;
  };
}

export interface ProductDetail {
  product: {
    sku: string;
    name: string;
    isActive: boolean;
    isExcludedFromPlanning: boolean;
    trackedViaParentOnly: boolean;
    leadTimeOverride: number | null;
    moqOverride: number | null;
    packSizeOverride: number | null;
    minimumStockQuantity: number | null;
    costCents: number | null;
    priceCents: number | null;
    supplier: {
      id: string;
      name: string;
      number: string;
    } | null;
  };
  kapitalbindung: number | null;
  metric: {
    dailyUsage: number;
    onHand: number;
    warehouseBreakdown: WarehouseBreakdownEntry[] | null;
    onOrder: number;
    reserved: number;
    availableQty: number;
    coverageDays: number;
    oosDate: string | null;
    severity: string;
    recommendedOrderQty: number;
    riskValueCents: number | null;
    recentSpikes: RecentSpike[] | null;
  } | null;
  salesHistory: Array<{ date: string; qty: string }> | [];
  stockHistory: Array<{ date: string; onHand: number }> | [];
  openPurchases: Array<{
    poNumber: string;
    qtyOpen: number;
    etaDate: string | null;
    countsTowardsRecommendation: boolean;
    ignoredReason: 'status' | 'age' | null;
  }> | [];
  parameters: { default_lead_time_days: number; safety_days: number; target_cover_days: number } | null;
  components: Array<{
    sku: string;
    name: string | null;
    factorQty: number;
    severity: 'red' | 'yellow' | 'green' | null;
    coverageDays: number | null;
  }>;
  packagingUnits: Array<{
    veSku: string;
    name: string | null;
    quantity: number;
    avgDailyUsageVe: number;
    avgDailyUsageBase: number;
  }>;
  lastSyncAt: string | null;
}

export interface ProductOverrides {
  lead_time_override?: number | null;
  moq_override?: number | null;
  pack_size_override?: number | null;
}

export interface Settings {
  notificationEmail: string;
  timezone: string;
  notificationIntervalHours: number;
  notificationIntervalOptions: number[];
  adhocNotificationsEnabled: boolean;
  parameters: ParameterSettings | null;
  monitoredProductCategoryIds: string[];
  productCategories: ProductCategory[];
  weclapp: WeclappSettings | null;
}

export interface ProductCategory {
  id: string;
  name: string;
}

export interface ParameterSettings {
  lookbackDays: number;
  weight0_30: number;
  weight30_60: number;
  weight60_90: number;
  safetyDays: number;
  targetCoverDays: number;
  defaultLeadTimeDays: number;
  minDailyUsageFloor: number;
  overstockFactor: number;
  noSalesDays: number;
  includeOpenPosInRecommendation: boolean;
  safetyStockServiceLevel: string;
  overdueIncomingGraceDays: number;
}

export interface CalculationTraceStep {
  label: string;
  expr: string;
  value: string | number | null;
}

export interface CalculationTraceSection {
  formulaId: string;
  formulaVersion: number;
  inputs: Record<string, unknown>;
  steps: CalculationTraceStep[];
  result: string | number | null;
  flags: string[];
}

export interface CalculationTrace {
  sku: string;
  computedAt: string;
  formulaVersions: Record<string, number>;
  trace: {
    traceVersion: number;
    computedAt: string;
    frozen: boolean;
    params: Record<string, unknown>;
    sections: Record<string, CalculationTraceSection>;
  };
}

export interface WeclappSettings {
  baseUrl: string;
  isConnected: boolean;
  lastSyncAt: string | null;
  lastSyncStatus: string;
  lastSyncError: string | null;
}

export interface Warehouse {
  id: string;
  name: string;
  warehouseType: string;
  monitored: boolean;
  shippingWarehouse: boolean;
  active: boolean;
}

export interface Alert {
  id: string;
  sku: string;
  productName: string;
  severity: 'red' | 'yellow' | 'green';
  lastSentAt: string | null;
  suppressUntil: string | null;
  isSuppressed: boolean;
  createdAt: string;
}

export interface SuppressedAlert {
  id: string,
  suppressUntil: string
}

export interface PurchaseOrderItem {
  sku: string;
  productName: string;
  supplierId: string | null;
  supplierName: string | null;
  recommendedQty: number;
  orderedQty: number;
  coverageDays: number | null;
  oosDate: string | null;
  onHand: number | null;
  dailyUsage: number | null;
  unit: string | null;
}

export interface PurchaseOrderDetail {
  id: string;
  tenantId: string;
  status: 'DRAFT' | 'ORDERED' | 'ARCHIVED';
  createdAt: string;
  orderedAt: string | null;
  archivedAt: string | null;
  generatedBy: string;
  generatedByName: string;
  itemCount: number;
  items: PurchaseOrderItem[];
}

export interface MasterDataSubstitution {
  value: string;
  unit: string;
  label: string;
  consequence: string;
}

export interface MasterDataFieldCoverage {
  field: string;
  label: string;
  erpField: string | null;
  scored: boolean;
  suppliedByErp: boolean;
  missing: {
    articleCount: number;
    articleShare: number;
    demandArticleCount: number;
    demandArticleShare: number;
    revenue: number;
    revenueShare: number;
  };
  substitution: MasterDataSubstitution;
}

export interface MasterDataNote {
  code: string;
  text: string;
}

export interface MasterDataCoverage {
  window: { lookbackDays: number; from: string; to: string };
  lastSyncAt: string | null;
  scope: { articleCount: number; demandArticleCount: number; revenueInWindow: number };
  headline: {
    weightedBy: string;
    coveredRevenueShare: number;
    affectedRevenueShare: number;
    affectedArticleShare: number;
    affectedDemandArticleShare: number;
  };
  fields: MasterDataFieldCoverage[];
  notes: MasterDataNote[];
}

export const api = new ApiClient();
export default api;
